import {
	Bell,
	Building2,
	FileCheck2,
	FileSearch,
	FileSpreadsheet,
	Network,
	Search,
	Users,
} from "lucide-react";
import {
	type SegmentLandingConfig,
	SegmentLandingPage,
} from "~/components/marketing/segment-landing-page";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "FirmIndex pro účetní a daňové poradce",
		description:
			"Podklady k prověření klienta, finanční závěrky a monitoring změn klientských firem na jednom místě. Vyzkoušejte FirmIndex zdarma.",
		canonical: `${SITE_URL}/pro-ucetni-a-danove-poradce`,
		noindex: true,
	});
}

const config = {
	eyebrow: "Pro externí účetní a daňové kanceláře",
	titleLead: "Mějte klientské firmy pod kontrolou.",
	titleHighlight: "Od onboardingu po každou důležitou změnu.",
	description:
		"Spojte si veřejné podklady o klientovi, finanční vývoj, osoby ve firmě a monitoring změn do jednoho pracovního přehledu.",
	heroNote:
		"Rozsah údajů se odvíjí od zveřejněných podkladů. FirmIndex je informační nástroj, nikoli kompletní AML/KYC řešení.",
	proofPoints: ["14 dní Premium", "Bez platební karty", "Monitoring klientských firem"],
	preview: {
		overline: "Klientské portfolio",
		title: "Co se změnilo u klientů",
		subtitle: "Přehled událostí bez pravidelného obcházení jednotlivých registrů.",
		badge: "2 nové změny",
		items: [
			{
				icon: Bell,
				label: "Monitoring klienta",
				value: "Změna statutárního orgánu",
			},
			{
				icon: FileSpreadsheet,
				label: "Nová listina",
				value: "Účetní závěrka zveřejněna",
			},
			{
				icon: Network,
				label: "Vztahy ve skupině",
				value: "Dostupné ze zveřejněné zprávy",
			},
		],
	},
	valueOverline: "Méně rutiny v klientské práci",
	valueHeading: "Podklady, které potřebujete před převzetím klienta i během spolupráce",
	valueDescription:
		"FirmIndex pomáhá dát veřejné informace do souvislostí, aby první prověření ani následná kontrola nezačínaly pokaždé od nuly.",
	valueCards: [
		{
			icon: Search,
			title: "Rychlejší první prověření",
			description:
				"Registrační údaje, osoby, insolvence, DPH a dostupné vazby najdete podle názvu nebo IČO v jednom profilu.",
		},
		{
			icon: Bell,
			title: "Klientské portfolio bez slepých míst",
			description:
				"Sledujte nové listiny, změny ve vedení nebo insolvenci u firem, které jsou pro vaši kancelář důležité.",
		},
		{
			icon: FileSpreadsheet,
			title: "Závěrky bez ručního přepisování",
			description:
				"Dostupná čísla z účetních výkazů uvidíte ve strukturovaném přehledu a můžete se vrátit k původní listině.",
		},
	],
	workflowHeading: "Jedna firma, jeden přehled, průběžné změny",
	workflowDescription:
		"Začněte u konkrétního klienta. Jakmile vidíte, že vám přehled šetří čas, přidejte do monitoringu další firmy z portfolia.",
	workflowSteps: [
		{
			icon: Building2,
			title: "Najděte klienta podle IČO",
			description:
				"Otevřete sjednocený profil s dostupnými registračními, finančními a dokumentovými podklady.",
		},
		{
			icon: FileCheck2,
			title: "Projděte finance a souvislosti",
			description:
				"Zkontrolujte vývoj, osoby ve firmě, zdrojové listiny a vztahy uvedené ve zveřejněných dokumentech.",
		},
		{
			icon: Bell,
			title: "Zapněte monitoring",
			description:
				"Důležité změny pak nemusíte hledat opakovaně — objeví se v přehledu sledovaných firem.",
		},
	],
	depthOverline: "Výhoda oproti samotným registrům",
	depthHeading: "Nejen veřejný výpis. Také obsah zveřejněných listin.",
	depthDescription:
		"Právě v dokumentech mohou být souvislosti, které v základním výpisu firmy snadno přehlédnete.",
	depthCards: [
		{
			icon: Network,
			title: "Vztahy uvnitř skupiny",
			description:
				"Ze zveřejněných zpráv o vztazích zpřehledňujeme uvedené ovládající a propojené osoby i významné vztahy.",
		},
		{
			icon: Users,
			title: "Lidé a jejich role v čase",
			description:
				"Aktuální i historické role pomáhají zasadit změnu ve vedení nebo vlastnické struktuře do kontextu.",
		},
		{
			icon: FileSearch,
			title: "Zdroj zůstává dohledatelný",
			description:
				"U vytěžených údajů se můžete vrátit k dokumentu, ze kterého informace pochází, a sami ji ověřit.",
		},
	],
	legalHeading: "Podpora vašeho procesu, ne automatický AML verdikt",
	legalDescription:
		"FirmIndex soustřeďuje veřejně dostupné podklady, které mohou pomoci při vlastním prověření klienta a průběžné práci s jeho firemními údaji.",
	legalNote:
		"FirmIndex nenahrazuje identifikaci a kontrolu klienta, sankční screening ani posouzení rizika podle AML zákona. Údaje o vztazích čerpá z veřejných listin; nejde o výpis z neveřejné části Evidence skutečných majitelů.",
	trialHeading: "Vyzkoušejte FirmIndex na firmách, které už znáte",
	trialDescription:
		"Porovnejte přehled s vlastní klientskou dokumentací a zjistěte, kde vám ušetří nejvíc hledání. Po 14 dnech účet automaticky pokračuje v plánu Zdarma.",
} satisfies SegmentLandingConfig;

export default function ProUcetniADanovePoradce() {
	return <SegmentLandingPage config={config} />;
}
